import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from './api';

export const KNOWN_GUIDANCE_KEYS = [
  'seen_welcome',
  'seen_health_intro',
  'seen_pill_hint',
  'seen_rounds_intro',
  'seen_rounds_card_hint',
  'seen_plan_intro',
];

function storageKey(key) {
  return `aroidex_seen_${key}`;
}

function normalizeSeen(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).map(([key, seen]) => [key, Boolean(seen)])
  );
}

function readLocalCache() {
  const seen = {};
  if (typeof localStorage === 'undefined') return seen;
  KNOWN_GUIDANCE_KEYS.forEach((key) => {
    const cached = localStorage.getItem(storageKey(key));
    if (cached === '1') seen[key] = true;
    if (cached === '0') seen[key] = false;
  });
  return seen;
}

export const GuidanceContext = createContext({
  hasSeen: () => false,
  markSeen: () => {},
  resetAllTips: async () => ({ ok: false }),
  guidanceSeen: {},
});

export function GuidanceProvider({ session, onSessionChange, children }) {
  const [guidanceSeen, setGuidanceSeen] = useState(() => ({
    ...readLocalCache(),
    ...normalizeSeen(session?.user?.guidance_seen),
  }));

  useEffect(() => {
    const serverSeen = normalizeSeen(session?.user?.guidance_seen);
    const hydrated = { ...serverSeen };

    KNOWN_GUIDANCE_KEYS.forEach((key) => {
      const seen = serverSeen[key] === true;
      hydrated[key] = seen;
      if (seen) localStorage.setItem(storageKey(key), '1');
      else localStorage.removeItem(storageKey(key));
    });

    setGuidanceSeen((prev) => ({ ...prev, ...hydrated }));
  }, [session?.user?.guidance_seen]);

  const markSeen = useCallback((key, seen = true) => {
    if (!key) return;
    const normalized = Boolean(seen);
    setGuidanceSeen((prev) => ({ ...prev, [key]: normalized }));
    if (normalized) localStorage.setItem(storageKey(key), '1');
    else localStorage.removeItem(storageKey(key));

    apiFetch('/users/me', {
      method: 'PUT',
      body: JSON.stringify({ guidance_seen: { [key]: normalized } }),
    })
      .then(async (res) => {
        if (!res.ok) return;
        const updated = await res.json();
        onSessionChange?.(updated);
      })
      .catch(() => {});
  }, [onSessionChange]);

  const resetAllTips = useCallback(async (keys = KNOWN_GUIDANCE_KEYS) => {
    const patch = {};
    const previous = guidanceSeen;
    keys.forEach((key) => {
      patch[key] = false;
      localStorage.removeItem(storageKey(key));
    });
    setGuidanceSeen((prev) => ({ ...prev, ...patch }));

    try {
      const res = await apiFetch('/users/me', {
        method: 'PUT',
        body: JSON.stringify({ guidance_seen: patch }),
      });
      if (!res.ok) throw new Error(`Reset failed (${res.status})`);
      const updated = await res.json();
      onSessionChange?.(updated);
      return { ok: true };
    } catch (err) {
      setGuidanceSeen(previous);
      keys.forEach((key) => {
        if (previous[key]) localStorage.setItem(storageKey(key), '1');
        else localStorage.removeItem(storageKey(key));
      });
      return { ok: false, error: err.message || 'Reset failed' };
    }
  }, [guidanceSeen, onSessionChange]);

  const value = useMemo(() => ({
    guidanceSeen,
    hasSeen: (key) => {
      if (guidanceSeen[key] !== undefined) return Boolean(guidanceSeen[key]);
      return localStorage.getItem(storageKey(key)) === '1';
    },
    markSeen,
    resetAllTips,
  }), [guidanceSeen, markSeen, resetAllTips]);

  return (
    <GuidanceContext.Provider value={value}>
      {children}
    </GuidanceContext.Provider>
  );
}

export function useGuidance() {
  return useContext(GuidanceContext);
}
